import { useEffect, useRef, useState } from 'react';
import { getSessionEmpresaId } from '../../../../../utils/SessionUser';


const EMPTY_FORM = {
    idAviso: 0,
    avisoContenido: '',
    idEmpresa: '',
    fechaCaducidad: '',
    activo: true,
};

export default function AvisosPrivacidadModal({
    open,
    mode = 'new',
    data = null,
    onClose,
    onSubmit,
    inline = false,
    empresaOptions = [],
}) {
    const [form, setForm] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({ avisoContenido: '', fechaCaducidad: '' });
    const contentInputRef = useRef(null);

    const userEmpresaId = getSessionEmpresaId();
    const isSuperAdmin = !userEmpresaId || userEmpresaId === 0 || userEmpresaId === '0';


    useEffect(() => {
        if (!open) return;
        if (mode === 'edit' && data) {
            setForm({
                idAviso: data.idAviso ?? 0,
                avisoContenido: data.avisoContenido ?? '',
                idEmpresa: data.idEmpresa != null ? String(data.idEmpresa) : '',
                fechaCaducidad: data.fechaCaducidad ? new Date(data.fechaCaducidad).toISOString().split('T')[0] : '',
                activo: data.activo ?? true,
            });
        } else {
            setForm({ 
                ...EMPTY_FORM,
                idEmpresa: !isSuperAdmin ? String(userEmpresaId) : ''
            });
            setErrors({ avisoContenido: '', fechaCaducidad: '' });
        }
    }, [open, mode, data, isSuperAdmin, userEmpresaId]);


    const validateField = (field, value) => {
        let error = '';
        if (field === 'avisoContenido' && !value?.trim()) {
            error = 'El contenido del aviso es requerido.';
        }
        if (field === 'fechaCaducidad' && !value) {
            error = 'La fecha de caducidad es requerida.';
        }
        setErrors((prev) => ({ ...prev, [field]: error }));
        return !error;
    };

    const handleChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
        validateField(field, value);
    };

    const handleSubmit = () => {
        const isContentValid = validateField('avisoContenido', form.avisoContenido);
        const isDateValid = validateField('fechaCaducidad', form.fechaCaducidad);
        if (!isContentValid || !isDateValid) return;
        
        // Mapeo PascalCase para asegurar compatibilidad total con C# DTO
        const payload = {
            AvisoContenido: form.avisoContenido,
            IdEmpresa: (form.idEmpresa === '' || form.idEmpresa == null) ? null : Number(form.idEmpresa),
            FechaCaducidad: form.fechaCaducidad === '' ? null : form.fechaCaducidad,
            Activo: form.activo
        };
        
        onSubmit?.(payload, mode);
    };

    const isSaveDisabled = 
        !form.avisoContenido?.trim() || 
        !form.fechaCaducidad || 
        !!errors.avisoContenido || 
        !!errors.fechaCaducidad;

    if (!open) return null;

    const Header = (
        <div className="roles-topbar" style={{ paddingRight: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: '0 0 auto' }}>
            <h3 className="roles-title">
                {mode === 'new' ? 'Nuevo Aviso de Privacidad' : 'Editar Aviso de Privacidad'}
            </h3>
            <button className="roles-btn" onClick={onClose}>✕</button>
        </div>
    );

    const Body = (
        <div style={{ padding: '0 16px 16px 16px', display: 'grid', gridTemplateColumns: '1fr', gap: '1.2rem', overflowY: 'auto', flex: '1 1 auto' }}>
            <div style={{ marginTop: '0.5rem' }}>
                <label className="roles-title" style={{ fontSize: '.9rem' }}>Contenido del Aviso de Privacidad *</label>
                <textarea
                    className={`infoField ${errors.avisoContenido ? 'infoField--error' : ''}`}
                    ref={contentInputRef}
                    value={form.avisoContenido}
                    onChange={(e) => handleChange('avisoContenido', e.target.value)}
                    style={{ minHeight: '180px', width: '100%', marginTop: '0.6rem' }}
                    placeholder="Escriba aquí el texto oficial del aviso de privacidad..."
                />
                {errors.avisoContenido && (
                    <small style={{ color: '#b91c1c' }}>{errors.avisoContenido}</small>
                )}
            </div>

            <div style={{ maxWidth: '300px' }}>
                <label className="roles-title" style={{ fontSize: '.9rem' }}>Fecha de Caducidad *</label>
                <input
                    type="date"
                    className={`infoField ${errors.fechaCaducidad ? 'infoField--error' : ''}`}
                    value={form.fechaCaducidad}
                    onChange={(e) => handleChange('fechaCaducidad', e.target.value)}
                    style={{ width: '100%', marginTop: '0.6rem' }}
                />
                {errors.fechaCaducidad && (
                    <small style={{ color: '#b91c1c', display: 'block', marginTop: 4 }}>{errors.fechaCaducidad}</small>
                )}
                <small style={{ color: '#64748b', fontSize: '.75rem', marginTop: 4, display: 'block' }}>
                    * Requerido: El aviso dejará de mostrarse después de esta fecha.
                </small>
            </div>
        </div>
    );

    const Footer = (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '.6rem', padding: '16px', flex: '0 0 auto' }}>
            <button className="roles-btn" onClick={onClose}>Cancelar</button>
            <button 
                className="btn-action" 
                onClick={handleSubmit}
                disabled={isSaveDisabled}
                style={{
                    opacity: isSaveDisabled ? 0.5 : 1,
                    cursor: isSaveDisabled ? 'not-allowed' : 'pointer',
                }}
            >
                {mode === 'new' ? 'Guardar' : 'Actualizar'}
            </button>
        </div>
    );

    if (inline) {
        return (
            <div className="roles-inline-card" style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {Header}
                {Body}
                {Footer}
            </div>
        );
    }

    return (
        <div className="roles-confirm-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
            <div className="roles-confirm-card" style={{ width: 'min(700px, 95vw)', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
                {Header}
                {Body}
                {Footer}
            </div>
        </div>
    );
}
